/**
 * Faction AI Personalities - defines unique AI behavior patterns for each faction
 */

import type { FactionId } from '@/types';

export interface AIPersonality {
  aggression: number;      // 0-1: How likely to attack
  expansion: number;       // 0-1: How eager to expand territory
  diplomacy: number;       // 0-1: How likely to form alliances
  economy: number;         // 0-1: Priority on economic buildings
  riskTolerance: number;   // 0-1: Willingness to take risks
  preferredUnitTypes: string[];  // Unit types this faction prefers
  // Building preferences for each faction (0-1 priority multiplier)
  buildingPreferences: {
    barracks: number;   // 剑兵营
    archery: number;    // 射箭场
    spearcamp: number;  // 枪兵营
    stable: number;     // 马厩
    smithy: number;     // 铁匠铺
    temple: number;     // 道观
    tower: number;      // 箭塔
    wall: number;       // 城墙
    farm: number;       // 农场
    mine: number;       // 矿场
    market: number;     // 市场
  };
}

export const FACTION_PERSONALITIES: Record<FactionId, AIPersonality> = {
  yan: {
    // 炎国 - Aggressive warmongers, attack-focused (+15% attack)
    // Strategy: Rush with offensive units, lots of barracks and cavalry
    aggression: 0.9,
    expansion: 0.8,
    diplomacy: 0.2,
    economy: 0.4,
    riskTolerance: 0.8,
    preferredUnitTypes: ['sword', 'cavalry'],
    buildingPreferences: {
      barracks: 1.0,   // Main unit production
      archery: 0.7,    // Ranged support
      spearcamp: 0.5,  // Anti-cavalry
      stable: 0.9,     // Fast attacks
      smithy: 0.3,     // Low priority - too defensive
      temple: 0.4,     // Magic support
      tower: 0.3,      // Minimal defense
      wall: 0.2,       // Low priority - prefer offense
      farm: 0.5,       // Basic economy
      mine: 0.6,       // Gold for army
      market: 0.3,     // Low priority
    },
  },
  han: {
    // 寒国 - Defensive fortress, slow but steady (+20% defense)
    // Strategy: Build strong defenses, heavy infantry, turtle
    aggression: 0.3,
    expansion: 0.4,
    diplomacy: 0.5,
    economy: 0.7,
    riskTolerance: 0.3,
    preferredUnitTypes: ['shield', 'spear'],
    buildingPreferences: {
      barracks: 0.6,   // Basic troops
      archery: 0.5,    // Defensive archers
      spearcamp: 0.9,  // Strong infantry
      stable: 0.3,     // Low priority - prefer infantry
      smithy: 1.0,     // Shield bearers for defense
      temple: 0.4,     // Some magic
      tower: 0.9,      // Strong defense
      wall: 1.0,       // High priority - fortifications
      farm: 0.8,       // Sustainable economy
      mine: 0.7,       // Steady income
      market: 0.5,     // Trade
    },
  },
  lin: {
    // 林国 - Balanced forest dwellers (no bonuses, versatile)
    // Strategy: Balanced approach with archers in forests
    aggression: 0.5,
    expansion: 0.6,
    diplomacy: 0.4,
    economy: 0.5,
    riskTolerance: 0.5,
    preferredUnitTypes: ['archer', 'sword'],
    buildingPreferences: {
      barracks: 0.8,   // Solid infantry
      archery: 1.0,    // Forest archers specialty
      spearcamp: 0.6,  // Some spears
      stable: 0.5,     // Some cavalry
      smithy: 0.5,     // Some shields
      temple: 0.5,     // Some magic
      tower: 0.6,      // Moderate defense
      wall: 0.5,       // Moderate walls
      farm: 0.7,       // Good economy
      mine: 0.6,       // Balanced income
      market: 0.5,     // Trade
    },
  },
  shang: {
    // 商国 - Trade empire, wealthy merchants (+40% income)
    // Strategy: Economy first, buy mercenaries, use gold advantage
    aggression: 0.2,
    expansion: 0.3,
    diplomacy: 0.8,
    economy: 0.9,
    riskTolerance: 0.2,
    preferredUnitTypes: ['archer', 'taoist'],
    buildingPreferences: {
      barracks: 0.4,   // Minimal military
      archery: 0.6,    // Ranged defense
      spearcamp: 0.3,  // Low priority
      stable: 0.3,     // Low priority
      smithy: 0.4,     // Some defense
      temple: 0.8,     // Magic and culture
      tower: 0.7,      // Protect wealth
      wall: 0.6,       // Protect trade routes
      farm: 1.0,       // Maximum income
      mine: 1.0,       // Maximum gold
      market: 0.9,     // Trade boost
    },
  },
  xuan: {
    // 玄国 - Mystic sorcerers, mysterious (diplomatic)
    // Strategy: Temple focus, magic units, defensive with spells
    aggression: 0.4,
    expansion: 0.5,
    diplomacy: 0.9,
    economy: 0.6,
    riskTolerance: 0.4,
    preferredUnitTypes: ['taoist', 'archer'],
    buildingPreferences: {
      barracks: 0.4,   // Minimal melee
      archery: 0.7,    // Ranged support
      spearcamp: 0.4,  // Some infantry
      stable: 0.3,     // Low priority
      smithy: 0.4,     // Some defense
      temple: 1.0,     // Main specialty - magic
      tower: 0.8,      // Magical defense
      wall: 0.7,       // Protect temples
      farm: 0.6,       // Moderate economy
      mine: 0.5,       // Some income
      market: 0.6,     // Trade for resources
    },
  },
  tie: {
    // 铁国 - Industrial powerhouse, heavy metal (-10% speed, sturdy)
    // Strategy: Heavy infantry, strong production, slow but powerful
    aggression: 0.6,
    expansion: 0.5,
    diplomacy: 0.3,
    economy: 0.5,
    riskTolerance: 0.6,
    preferredUnitTypes: ['shield', 'spear'],
    buildingPreferences: {
      barracks: 0.7,   // Infantry production
      archery: 0.4,    // Low priority - prefer melee
      spearcamp: 0.9,  // Heavy infantry
      stable: 0.3,     // Low priority - too fast
      smithy: 1.0,     // Main specialty - heavy armor
      temple: 0.3,     // Low magic
      tower: 0.8,      // Strong fortifications
      wall: 0.9,       // Heavy fortifications
      farm: 0.6,       // Production needs food
      mine: 0.9,       // Lots of metal/gold
      market: 0.4,     // Some trade
    },
  },
  yun: {
    // 云国 - Swift nomads, mobile warfare (+35% speed)
    // Strategy: Fast cavalry raids, hit and run, mobile army
    aggression: 0.7,
    expansion: 0.7,
    diplomacy: 0.4,
    economy: 0.4,
    riskTolerance: 0.7,
    preferredUnitTypes: ['cavalry', 'archer'],
    buildingPreferences: {
      barracks: 0.5,   // Some infantry
      archery: 0.9,    // Horse archers
      spearcamp: 0.3,  // Low priority - too slow
      stable: 1.0,     // Main specialty - cavalry
      smithy: 0.2,     // Too slow and heavy
      temple: 0.4,     // Some magic
      tower: 0.4,      // Minimal defense - prefer mobility
      wall: 0.3,       // Low priority - prefer mobility
      farm: 0.6,       // Feed horses
      mine: 0.5,       // Some gold
      market: 0.5,     // Trade routes
    },
  },
  neutral: {
    // Neutral - no AI behavior
    aggression: 0,
    expansion: 0,
    diplomacy: 0,
    economy: 0,
    riskTolerance: 0,
    preferredUnitTypes: [],
    buildingPreferences: {
      barracks: 0, archery: 0, spearcamp: 0, stable: 0, smithy: 0,
      temple: 0, tower: 0, wall: 0, farm: 0, mine: 0, market: 0,
    },
  },
};

export type AIStrategy = 'aggressive' | 'defensive' | 'economic' | 'balanced';

/**
 * Determines the current strategy based on personality and game state
 */
export function determineStrategy(
  personality: AIPersonality,
  threatLevel: number,   // 0-1: How threatened is this faction
  economicStrength: number, // 0-1: Relative economic power
  militaryStrength: number  // 0-1: Relative military power
): AIStrategy {
  // If under serious threat, go defensive regardless of personality
  if (threatLevel > 0.7 && militaryStrength < 0.4) {
    return 'defensive';
  }

  // If we have strong economy but weak military, keep building economy or defend
  if (economicStrength > 0.6 && militaryStrength < 0.3) {
    return personality.riskTolerance > 0.5 ? 'balanced' : 'defensive';
  }

  // If strong military and aggressive personality, attack
  if (militaryStrength > 0.5 && personality.aggression > 0.6) {
    return 'aggressive';
  }

  // If economic personality is dominant, focus on economy
  if (personality.economy > 0.7 && threatLevel < 0.4) {
    return 'economic';
  }

  // Default to balanced
  return 'balanced';
}

/**
 * Gets the priority for different building types based on strategy and faction preferences
 */
export function getBuildingPriorities(
  strategy: AIStrategy,
  personality: AIPersonality
): Record<string, number> {
  // Start with faction's base building preferences
  const prefs = personality.buildingPreferences;

  // Strategy multipliers for each building type
  const strategyMods: Record<AIStrategy, Record<string, number>> = {
    aggressive: {
      barracks: 1.3, archery: 1.2, spearcamp: 1.0, stable: 1.4, smithy: 0.7,
      temple: 0.8, tower: 0.5, wall: 0.3, farm: 0.6, mine: 0.8, market: 0.5,
    },
    defensive: {
      barracks: 0.8, archery: 0.9, spearcamp: 1.1, stable: 0.6, smithy: 1.3,
      temple: 0.7, tower: 1.5, wall: 1.4, farm: 1.0, mine: 0.9, market: 0.7,
    },
    economic: {
      barracks: 0.5, archery: 0.5, spearcamp: 0.5, stable: 0.4, smithy: 0.5,
      temple: 0.8, tower: 0.7, wall: 0.5, farm: 1.5, mine: 1.4, market: 1.3,
    },
    balanced: {
      barracks: 1.0, archery: 1.0, spearcamp: 1.0, stable: 1.0, smithy: 1.0,
      temple: 1.0, tower: 1.0, wall: 0.8, farm: 1.0, mine: 1.0, market: 1.0,
    },
  };

  const mods = strategyMods[strategy];

  // Combine faction preferences with strategy modifiers
  return {
    barracks: prefs.barracks * mods.barracks,
    archery: prefs.archery * mods.archery,
    spearcamp: prefs.spearcamp * mods.spearcamp,
    stable: prefs.stable * mods.stable,
    smithy: prefs.smithy * mods.smithy,
    temple: prefs.temple * mods.temple,
    tower: prefs.tower * mods.tower,
    farm: prefs.farm * mods.farm,
    mine: prefs.mine * mods.mine,
    market: prefs.market * mods.market,
  };
}
